import { makeAutoObservable, runInAction } from 'mobx';
import {
  IAuthDataSource,
  RegistrationGoal,
  UNEXPECTED_ERROR_KEY,
} from '../../../../../infrastructure/types/features/auth/auth-types';
import type { IMetrikaService } from '../../../../../../shared/infrastructure/types/common/metrika-types';
import {
  LoginErrorDataResponse,
  LoginRequest,
  LoginResponse,
} from '../../../../../infrastructure/types/features/auth/auth-contracts';
import {
  ExternalNotification,
  LoadingStatus,
  SubmittingStatus,
} from '../../../../../../shared/infrastructure/types/common/common-types';
import { ResponseStatus } from '../../../../../../shared/infrastructure/types/api/api-types';
import { UiNotificationType } from '../../../../../../shared/infrastructure/types/common/common-types';
import { LoginValidator } from '../validators/LoginValidator';
import { ipLookup, DEFAULT_IP } from '../../../../../infrastructure/utils/ip-lookup';
import { YandexCloudManager } from './YandexCloudManager';

class LoginManager {
  login?: LoginEmail;
  password?: LoginPassword;
  pageErrorKey?: string;
  private ipAddress: IpAddress = '' as IpAddress;
  private registrationStartPage?: MetrikaStartPage;

  validator: LoginValidator;

  constructor(
    private datasource: IAuthDataSource,
    private yandexCloudManager: YandexCloudManager,
    private getLoginStatus: () => SubmittingStatus,
    private getLoginLoadingStatus: () => LoadingStatus,
    private setLoginFormStatus: (status: SubmittingStatus) => void,
    private setLoginLoadingStatus: (status: LoadingStatus) => void,
    private showNotification: (notification: ExternalNotification) => void,
    private openDefaultPage: () => void,
    private navigateToPath: (path: RoutePath) => void,
    private readonly metrikaService: IMetrikaService<RegistrationGoal>,
  ) {
    makeAutoObservable(this, {});
    this.validator = new LoginValidator(this);
    this.resolveIp();
  }

  private resolveIp(): Promise<IpAddress> {
    return ipLookup.getIP().then(ip => {
      runInAction(() => {
        this.ipAddress = ip as IpAddress;
      });
      return ip as IpAddress;
    });
  }

  private async actualizeIp(): Promise<IpAddress> {
    if (this.ipAddress && this.ipAddress !== DEFAULT_IP) {
      return this.ipAddress;
    }
    return this.resolveIp();
  }

  get loginFormStatus() {
    return this.getLoginStatus();
  }

  get loginLoadingStatus() {
    return this.getLoginLoadingStatus();
  }

  setLogin(login: LoginEmail): void {
    this.login = login;
  }

  setPassword(password: LoginPassword): void {
    this.password = password;
  }

  setPageError(key?: string) {
    this.pageErrorKey = key;
  }

  startRegistrationFlow(startPage: MetrikaStartPage) {
    this.registrationStartPage = startPage;
  }

  private trackRegistrationLogin() {
    if (!this.registrationStartPage) {
      return;
    }
    this.metrikaService.reachGoal(RegistrationGoal.SERVICE_LOGIN, this.registrationStartPage);
    this.resetRegistrationStartPage();
  }

  private resetRegistrationStartPage(): void {
    this.registrationStartPage = undefined;
  }

  async submitLogin(): Promise<void> {
    this.pageErrorKey = undefined;
    this.yandexCloudManager.clearLoginError();

    if (!this.yandexCloudManager.refreshValidation()) {
      this.setLoginFormStatus(SubmittingStatus.ERROR_SUBMITTING);
      this.setLoginLoadingStatus(LoadingStatus.LOADED);
      this.resetRegistrationStartPage();
      return;
    }

    if (this.validator.hasErrors) {
      this.setLoginFormStatus(SubmittingStatus.ERROR_SUBMITTING);
      this.resetRegistrationStartPage();
      return;
    }

    this.setLoginFormStatus(SubmittingStatus.SUBMITTING);
    this.setLoginLoadingStatus(LoadingStatus.LOADING);

    const ipAddress = await this.actualizeIp();

    const request: LoginRequest = {
      user: {
        email: this.login as LoginEmail,
        password: this.password as LoginPassword,
        ip_address: ipAddress,
        tz_offset: new Date().getTimezoneOffset() as TimezoneOffset,
      },
    };
    const yandexCloud = this.yandexCloudManager.data;
    if (yandexCloud) {
      request.user.yandex_cloud = yandexCloud;
    }

    this.datasource.login(request).then(response => {
      runInAction(() => {
        if (response.status === ResponseStatus.SUCCESS) {
          this.handleLoginSuccess(response.result);
          return;
        }

        this.setLoginLoadingStatus(LoadingStatus.LOADED);
        this.resetRegistrationStartPage();

        if (response.status === ResponseStatus.SERVICE_ERROR) {
          const error = (response.error.errorData as LoginErrorDataResponse)?.error;
          if (this.yandexCloudManager.setLoginError(error)) {
            return;
          }
          this.showNotification({
            text: error as unknown as LocalizedMessageCode,
            type: UiNotificationType.ERROR,
          });
        }
      });
    });
  }

  private handleLoginSuccess(response: LoginResponse): void {
    this.datasource.executeAuthCallback(response.callback_data).then(authCallbackResponse => {
      runInAction(() => {
        this.setLoginLoadingStatus(LoadingStatus.LOADED);
        if (authCallbackResponse.status === ResponseStatus.SUCCESS) {
          this.yandexCloudManager.clear();
          this.trackRegistrationLogin();
          const location = authCallbackResponse.result?.location;
          if (location) {
            this.navigateToPath(location as RoutePath);
            return;
          }
          this.openDefaultPage();
          return;
        }

        this.resetRegistrationStartPage();
        this.datasource.logout();
        this.showNotification({
          text: UNEXPECTED_ERROR_KEY as LocalizedMessageCode,
          type: UiNotificationType.ERROR,
        });
      });
    });
  }
}

export { LoginManager };
